// @ts-nocheck
import { useState, useRef } from "react";
import { Upload, X } from "lucide-react";
import { SubSection, FieldLabel, inputClass } from "./SectionWrapper";
import { EventData } from "@/types/events";
import { supabase } from "@/integrations/supabase/client";
import WysiwygEditor from "@/components/ui/wysiwyg-editor";

// StayToPlaySubsection moved to HotelsSection step
import { toast } from "sonner";

interface Props {
  event: EventData;
  updateEvent: (updates: Partial<EventData>) => void;
}

export function DetailsSection({ event, updateEvent }: Props) {
  const bgInputRef = useRef<HTMLInputElement>(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);
  const [uploadingBg, setUploadingBg] = useState(false);
  const [uploadingGallery, setUploadingGallery] = useState(false);

  const handleBgUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      alert("File must be under 10MB");
      return;
    }
    setUploadingBg(true);
    const ext = file.name.split(".").pop();
    const path = `bg-${event.id ?? "new"}-${Date.now()}.${ext}`;
    const { data, error } = await supabase.storage.from("event-assets").upload(path, file, { upsert: true });
    if (error) {
      console.error("[bg] upload failed", error);
      toast.error(`Background upload failed: ${error.message}`);
    } else if (data) {
      const { data: urlData } = supabase.storage.from("event-assets").getPublicUrl(data.path);
      updateEvent({ background_image_url: urlData.publicUrl });
    }
    setUploadingBg(false);
  };

  const handleGalleryUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files?.length) return;
    setUploadingGallery(true);
    const newUrls: string[] = [];
    for (const file of Array.from(files)) {
      if (file.size > 10 * 1024 * 1024) continue;
      const ext = file.name.split(".").pop();
      const path = `gallery-${event.id ?? "new"}-${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
      const { data, error } = await supabase.storage.from("event-assets").upload(path, file, { upsert: true });
      if (error) {
        console.error("[gallery] upload failed", error);
        toast.error(`Gallery upload failed: ${error.message}`);
        continue;
      }
      if (data) {
        const { data: urlData } = supabase.storage.from("event-assets").getPublicUrl(data.path);
        newUrls.push(urlData.publicUrl);
      }
    }
    if (newUrls.length > 0) {
      // Use the latest persisted array (not the closure value, which may be stale
      // between renders/uploads) by reading from `event` at call-time.
      const next = [...(event.gallery_images ?? []), ...newUrls];
      updateEvent({ gallery_images: next });
    }
    // Reset the input so re-selecting the same file re-fires onChange.
    if (galleryInputRef.current) galleryInputRef.current.value = "";
    setUploadingGallery(false);
  };

  const removeGalleryImage = (index: number) => {
    updateEvent({ gallery_images: (event.gallery_images ?? []).filter((_, i) => i !== index) });
  };

  return (
    <div className="space-y-6">
      {/* MEDIA — at the top */}
      <SubSection title="Media">
        <p className="text-xs text-muted-foreground -mt-1 mb-3">
          Thumbnail set in Basic Info →
        </p>

        {/* Promo Video */}
        <div>
          <FieldLabel>YouTube Promo Video URL</FieldLabel>
          <input
            type="url"
            value={event.promo_video_url}
            onChange={(e) => updateEvent({ promo_video_url: e.target.value })}
            placeholder="https://youtube.com/..."
            className={inputClass}
          />
        </div>

        {/* Gallery */}
        <div>
          <FieldLabel>Gallery Images</FieldLabel>
          {(event.gallery_images ?? []).length > 0 && (
            <div className="flex flex-wrap gap-2 mb-2">
              {(event.gallery_images ?? []).map((url, i) => (
                <div key={i} className="relative">
                  <img src={url} alt={`Gallery ${i + 1}`} className="w-20 h-20 object-cover rounded border border-border" />
                  <button
                    onClick={() => removeGalleryImage(i)}
                    className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white rounded-full flex items-center justify-center text-xs"
                  >
                    <X className="w-2.5 h-2.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
          <button
            onClick={() => galleryInputRef.current?.click()}
            disabled={uploadingGallery}
            className="flex items-center gap-2 px-4 py-2 border border-dashed border-border rounded-lg text-sm text-muted-foreground hover:border-primary hover:text-primary transition-colors"
          >
            <Upload className="w-4 h-4" />
            {uploadingGallery ? "Uploading..." : "Add Gallery Images"}
          </button>
          <input ref={galleryInputRef} type="file" accept="image/*" multiple onChange={handleGalleryUpload} className="hidden" />
        </div>
      </SubSection>

      {/* DISPLAY SETTINGS — rich text */}
      <SubSection title="Display Settings">
        <div>
          <FieldLabel>Event Format</FieldLabel>
          <WysiwygEditor
            value={event.event_format || ""}
            onChange={(v) => updateEvent({ event_format: v })}
            placeholder="Pool play followed by single elimination bracket..."
          />
        </div>

        <div>
          <FieldLabel>Check-in Instructions</FieldLabel>
          <div className="mt-3">
            <WysiwygEditor
              value={event.checkin_instructions || ""}
              onChange={(v) => updateEvent({ checkin_instructions: v })}
              placeholder="Check-in begins at 7:00 AM at the main entrance..."
            />
          </div>
        </div>

        <div>
          <FieldLabel>Rules</FieldLabel>
          <div className="mt-3">
            <WysiwygEditor
              value={event.rules || ""}
              onChange={(v) => updateEvent({ rules: v })}
              placeholder="Standard NFHS rules apply..."
            />
          </div>
        </div>

        <div>
          <FieldLabel>Pricing Notes</FieldLabel>
          <WysiwygEditor
            value={event.pricing_notes || ""}
            onChange={(v) => updateEvent({ pricing_notes: v })}
            placeholder="Early bird pricing available until..."
          />
        </div>

        <div>
          <FieldLabel>Age Groups Description</FieldLabel>
          <WysiwygEditor
            value={event.age_groups_description || ""}
            onChange={(v) => updateEvent({ age_groups_description: v })}
            placeholder="Age is determined by grade as of September 1..."
          />
        </div>
      </SubSection>

      {/* LIVE STREAMING — its own mini-section */}
      <SubSection title="Live Streaming">
        <div>
          <FieldLabel>Live Streaming Description</FieldLabel>
          <WysiwygEditor
            value={event.live_streaming_description || ""}
            onChange={(v) => updateEvent({ live_streaming_description: v })}
            placeholder="Describe live streaming for this event..."
          />
        </div>

        <div>
          <FieldLabel>Live Streaming URL</FieldLabel>
          <input
            type="url"
            value={event.live_streaming_url}
            onChange={(e) => updateEvent({ live_streaming_url: e.target.value })}
            placeholder="https://..."
            className={inputClass}
          />
        </div>
      </SubSection>

      {/* Stay to Play moved to dedicated "Hotels" step */}

      {/* Media Credentials and College Coaches moved to "Coaches / Media" step */}
      {/* Notification Settings moved to dedicated "Notifications" step */}
    </div>
  );
}
